import React from 'react';
import { motion } from 'framer-motion';
import { Heart, MessageCircle, Package, Lightbulb, HelpCircle, Clock, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import ProfessionalBadge from '@/components/profile/ProfessionalBadge';

const POST_TYPE_CONFIG = {
  producto: { icon: Package, color: 'text-blue-600', bg: 'bg-blue-50' },
  tip: { icon: Lightbulb, color: 'text-amber-600', bg: 'bg-amber-50' },
  experiencia: { icon: MessageCircle, color: 'text-purple-600', bg: 'bg-purple-50' },
  pregunta: { icon: HelpCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
};

const CATEGORY_LABELS = {
  diabetes: 'Diabetes',
  hipertension: 'Hipertensión',
  alimentacion_familiar: 'Alimentación Familiar',
  lectura_etiquetas: 'Lectura de Etiquetas',
};

export default function PostCard({ post, onLike, onComment, onClick, currentUserId, isLiked, author }) {
  const config = POST_TYPE_CONFIG[post.post_type] || POST_TYPE_CONFIG.experiencia;
  const Icon = config.icon;
  const isAuthor = currentUserId === post.created_by;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
      className="bg-white rounded-2xl p-3 sm:p-4 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start gap-2 mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className={`w-9 h-9 rounded-xl ${config.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-4 h-4 ${config.color}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="font-medium text-gray-900 text-sm truncate">{post.author_name}</p>
              {author && <ProfessionalBadge user={author} size="sm" showLabel={false} />}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-400 flex-wrap">
              <span>{format(new Date(post.created_date), "d MMM", { locale: es })}</span>
              <span>•</span>
              <span className="truncate">{CATEGORY_LABELS[post.category]}</span>
            </div>
          </div>
        </div>
        
        {post.status === 'pending' && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-200 flex-shrink-0">
            <Clock className="w-3 h-3 text-amber-600" />
            <span className="text-xs font-medium text-amber-700 hidden xs:inline">Pendiente</span>
          </div>
        )}
        
        {post.status === 'approved' && isAuthor && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 flex-shrink-0">
            <CheckCircle className="w-3 h-3 text-emerald-600" />
            <span className="text-xs font-medium text-emerald-700 hidden xs:inline">Aprobado</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="mb-4">
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
          {post.title}
        </h3>
        <p className="text-gray-600 text-sm line-clamp-3">
          {post.content}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-4 pt-3 border-t border-gray-50">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onLike?.(post.id);
          }}
          className={`flex items-center gap-2 transition-colors ${
            isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
          }`}
        >
          <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
          <span className="text-sm font-medium">{post.likes_count || 0}</span>
        </button>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            onComment?.(post.id);
          }}
          className="flex items-center gap-2 text-gray-400 hover:text-emerald-600 transition-colors"
        >
          <MessageCircle className="w-4 h-4" />
          <span className="text-sm font-medium">{post.comments_count || 0}</span>
        </button>
      </div>
    </motion.div>
  );
}